import { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import toast from "react-hot-toast";
import { getCostosDeLote, actualizarCostoLote, validarOrdenFechasLote } from "../../utils/loteCostos";
import { actualizarLote, getLoteById } from "../../utils/lotesCompra";

// Editor de fecha de pago para las líneas de LOTE del tab CapEx (FOB + componentes), gemelo del
// ModificarFechaProyeccionCapexModal de los activos pero con destino y reglas propias del lote:
//   · Línea FOB (sin costo_id)  → actualizarLote(loteId, { fecha_planificada }).
//   · Línea componente (costo_id) → actualizarCostoLote(costoId, { fecha_pago }).
// A diferencia del editor de activo, NO hay piso HOY: la fecha del lote/componente admite vencidas
// (10.159). Antes de escribir se carga el cuadro REAL del lote (fecha_planificada del lote +
// componentes vía getCostosDeLote), se aplica la nueva fecha a la línea editada y se corre
// validarOrdenFechasLote (FOB ≤ Transporte < Aduana); si viola, bloquea con el mismo mensaje y NO escribe.

const normalizarFecha = (fechaStr) => {
  if (!fechaStr) return "";
  return String(fechaStr).includes("T") ? String(fechaStr).substring(0, 10) : String(fechaStr);
};

const formatMonto = (entry) => {
  const n = Math.abs(Number(entry?.importeEnMoneda || 0));
  const num = n.toLocaleString("es-CL", { maximumFractionDigits: 0 });
  const moneda = entry?.moneda_origen === "USD" ? "US$ " : "";
  return `${moneda}${num}`;
};

const ModificarFechaLoteCapexModal = ({ show, onHide, entry, onSaved }) => {
  const [fecha, setFecha] = useState(normalizarFecha(entry?.fecha_display));
  const [guardando, setGuardando] = useState(false);

  if (!entry) return null;

  // FOB vs componente: la línea de componente lleva costo_id (id real en lote_costos); la del FOB no.
  const esComponente = !!entry.costo_id;
  const tipoLabel = esComponente ? entry.folio || "Componente" : "FOB";

  const handleGuardar = async () => {
    if (!fecha) {
      toast.error("La fecha es obligatoria");
      return;
    }
    setGuardando(true);
    try {
      // Cuadro real del lote (releído de DB, no de la proyección que puede estar stale): fecha del FOB
      // + componentes. Se aplica la nueva fecha a la línea editada y se valida el orden antes de escribir.
      const costos = await getCostosDeLote(entry.lote_id);
      let fobFecha;
      let componentesParaValidar;
      if (esComponente) {
        const lote = await getLoteById(entry.lote_id);
        fobFecha = normalizarFecha(lote?.fecha_planificada);
        componentesParaValidar = costos.map((c) => (c.id === entry.costo_id ? { ...c, fecha_pago: fecha } : c));
      } else {
        fobFecha = fecha; // la línea editada ES el FOB
        componentesParaValidar = costos;
      }

      const violaciones = validarOrdenFechasLote(fobFecha, componentesParaValidar);
      if (violaciones.length > 0) {
        // Mensaje factual del helper (compartido con el modal Componentes) + hint accionable propio de
        // Caja: acá se edita una sola fecha por vez, así que si el bloqueo pide mover varias, el camino
        // es Componentes del lote. El helper queda genérico; el hint vive solo en este modal.
        toast.error(
          `${violaciones[0].mensaje} Para mover varias fechas juntas, editalas desde "Componentes" del lote.`
        );
        return; // no escribe ni FOB ni componente
      }

      await (esComponente
        ? actualizarCostoLote(entry.costo_id, { fecha_pago: fecha })
        : actualizarLote(entry.lote_id, { fecha_planificada: fecha }));
      toast.success("Fecha de pago actualizada");
      // Patch puntual (Fase 2A): el padre actualiza `componentesLotes` (por costo_id) o `capexLotes`
      // (por lote_id) según el entry, sin refetch.
      onSaved?.(fecha);
    } catch (e) {
      toast.error(`Error al guardar: ${e.message || e}`);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1.1rem" }}>Modificar fecha de pago</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ fontSize: "0.9rem", lineHeight: 1.7, marginBottom: 16 }}>
          <div>
            <strong>Concepto:</strong> {entry.razon_social_receptor || "—"}
          </div>
          <div>
            <strong>Tipo:</strong> {tipoLabel}
          </div>
          <div>
            <strong>Monto:</strong> {formatMonto(entry)}
          </div>
        </div>

        <Form.Group>
          <Form.Label style={{ fontWeight: 600, fontSize: "0.9rem" }}>Fecha de pago</Form.Label>
          <Form.Control
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            disabled={guardando}
            style={!fecha ? { borderColor: "#dc3545" } : undefined}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onHide} disabled={guardando}>
          Cancelar
        </Button>
        <Button variant="primary" size="sm" onClick={handleGuardar} disabled={guardando}>
          {guardando ? "Guardando…" : "Guardar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModificarFechaLoteCapexModal;
